import {
  ActionIcon,
  Box,
  Center,
  Divider,
  Group,
  Loader,
  Popover,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconBell,
  IconBellOff,
  IconCheck,
  IconChecks,
  IconExternalLink,
} from '@tabler/icons-react';
import { useHover } from '@mantine/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { Communication } from '@medplum/fhirtypes';
import { usePermissions } from '../contexts/PermissionContext';
import type { UseInAppNotificationsReturn } from '../hooks/useInAppNotifications';
import {
  getNotificationTitle,
  getNotificationBody,
  getNotificationTimestamp,
  getNotificationTargetReference,
  resolveNotificationRoute,
  isUnreadNotification,
} from '../lib/notificationFhir';

dayjs.extend(relativeTime);

type NotificationFilter = 'all' | 'unread';

interface NotificationsPopoverProps {
  opened: boolean;
  onClose: () => void;
  onToggle: () => void;
  notifications: UseInAppNotificationsReturn;
}

function formatTimestamp(iso: string | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso);
  if (!d.isValid()) return '';
  const now = dayjs();
  if (now.diff(d, 'hour') < 24) {
    return d.fromNow();
  }
  if (now.diff(d, 'day') < 7) {
    return d.format('ddd h:mm A');
  }
  return d.format('MMM D, YYYY');
}

function FilterPill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <UnstyledButton
      onClick={onClick}
      px={10}
      py={4}
      style={{
        borderRadius: 16,
        border: active ? '1px solid var(--mantine-color-gray-3)' : '1px solid transparent',
        backgroundColor: active ? 'var(--mantine-color-gray-1)' : 'transparent',
      }}
    >
      <Text size="xs" fw={active ? 600 : 400} c={active ? 'gray.8' : 'dimmed'}>
        {label}
      </Text>
    </UnstyledButton>
  );
}

function NotificationItem({
  comm,
  clientPortal,
  onMarkRead,
  onNavigate,
}: {
  comm: Communication;
  clientPortal: boolean;
  onMarkRead: (id: string) => void;
  onNavigate: (comm: Communication) => void;
}): JSX.Element {
  const unread = isUnreadNotification(comm);
  const title = getNotificationTitle(comm);
  const body = getNotificationBody(comm);
  const timestamp = getNotificationTimestamp(comm);
  const targetRef = getNotificationTargetReference(comm);
  const hasTarget = !!resolveNotificationRoute(targetRef, { clientPortal });
  const { hovered, ref } = useHover<HTMLDivElement>();

  let rowBackground: string | undefined;
  if (unread) {
    rowBackground = hovered
      ? 'var(--mantine-color-blue-1)'
      : 'var(--mantine-color-blue-0)';
  } else if (hovered) {
    rowBackground = 'var(--mantine-color-gray-0)';
  }

  return (
    <Group
      ref={ref}
      wrap="nowrap"
      align="flex-start"
      gap={8}
      w="100%"
      py={10}
      px={16}
      style={{ backgroundColor: rowBackground }}
    >
      <Box flex={1} miw={0}>
        <UnstyledButton
          w="100%"
          onClick={() => onNavigate(comm)}
          style={{ textAlign: 'left' }}
        >
          <Group wrap="nowrap" gap={4} align="center">
            <Text
              size="sm"
              fw={unread ? 600 : 500}
              lineClamp={1}
              style={{ flex: 1, minWidth: 0 }}
            >
              {title}
            </Text>
            {hasTarget && (
              <IconExternalLink
                size={14}
                stroke={1.5}
                style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }}
              />
            )}
          </Group>
          {body && (
            <Text size="xs" c="dimmed" lineClamp={2} mt={2}>
              {body}
            </Text>
          )}
        </UnstyledButton>
        {(timestamp || unread) && (
          <Group gap={4} mt={2} wrap="nowrap" align="center">
            {timestamp && (
              <Text size="xs" c="dimmed">
                {formatTimestamp(timestamp)}
              </Text>
            )}
            {unread && comm.id && (
              <Tooltip label="Mark as read" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  aria-label="Mark as read"
                  onClick={() => onMarkRead(comm.id!)}
                >
                  <IconCheck size={12} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        )}
      </Box>
      <Box
        mt={6}
        w={8}
        h={8}
        style={{
          borderRadius: '50%',
          backgroundColor: unread ? 'var(--mantine-color-blue-6)' : 'transparent',
          flexShrink: 0,
        }}
        aria-hidden
      />
    </Group>
  );
}

export function NotificationsPopover({
  opened,
  onClose,
  onToggle,
  notifications: {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
  },
}: NotificationsPopoverProps): JSX.Element {
  const navigate = useNavigate();
  const { isClient } = usePermissions();
  const [filter, setFilter] = useState<NotificationFilter>('all');

  const visibleNotifications = useMemo(
    () =>
      filter === 'unread'
        ? notifications.filter(isUnreadNotification)
        : notifications,
    [filter, notifications],
  );

  const handleNavigate = useCallback(
    async (comm: Communication) => {
      if (isUnreadNotification(comm) && comm.id) {
        void markAsRead(comm.id);
      }
      onClose();

      const targetRef = getNotificationTargetReference(comm);
      const route = resolveNotificationRoute(targetRef, { clientPortal: isClient });
      if (route) {
        navigate(route);
      }
    },
    [markAsRead, navigate, onClose, isClient],
  );

  const handleMarkRead = useCallback(
    (id: string) => {
      void markAsRead(id);
    },
    [markAsRead],
  );

  return (
    <Popover
      opened={opened}
      onChange={(nextOpened) => {
        if (!nextOpened) onClose();
      }}
      closeOnClickOutside
      closeOnEscape
      clickOutsideEvents={['mousedown', 'touchstart']}
      position="bottom-end"
      width={380}
      shadow="lg"
      radius="md"
      offset={8}
      withinPortal
      // The bell lives in the fixed AppShell header. With the default absolute
      // strategy the portaled dropdown is re-positioned on every scroll event to
      // chase the target, which reads as flashing.
      floatingStrategy="fixed"
    >
      <Popover.Target>
        <UnstyledButton
          pos="relative"
          style={{ display: 'flex', color: 'var(--mantine-color-gray-7)' }}
          onClick={onToggle}
          aria-label="Notifications"
        >
          <IconBell size={20} stroke={1.5} />
          {unreadCount > 0 && (
            <Box
              pos="absolute"
              top={2}
              right={2}
              w={6}
              h={6}
              style={{
                borderRadius: '50%',
                backgroundColor: 'var(--mantine-color-red-6)',
              }}
              aria-hidden
            />
          )}
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown p={0} style={{ overflow: 'hidden' }}>
        {/* Header */}
        <Group justify="space-between" px={16} py={12}>
          <Text fw={600} size="sm">
            Notifications
          </Text>
        </Group>

        <Divider />

        {/* Filter bar */}
        <Group justify="space-between" px={16} py={8} wrap="nowrap">
          <Group gap={4} wrap="nowrap">
            <FilterPill
              active={filter === 'all'}
              label={`All (${notifications.length})`}
              onClick={() => setFilter('all')}
            />
            <FilterPill
              active={filter === 'unread'}
              label="Unread"
              onClick={() => setFilter('unread')}
            />
          </Group>
          {unreadCount > 0 && (
            <UnstyledButton
              onClick={() => void markAllAsRead()}
              style={{ flexShrink: 0 }}
            >
              <Group gap={4} wrap="nowrap">
                <IconChecks
                  size={14}
                  stroke={1.5}
                  style={{ color: 'var(--mantine-color-blue-6)' }}
                />
                <Text size="xs" c="blue.6" td="underline">
                  Mark all as read
                </Text>
              </Group>
            </UnstyledButton>
          )}
        </Group>

        {/* Content */}
        <ScrollArea.Autosize mah={420} scrollbarSize={6}>
          {loading && (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          )}

          {!loading && error && (
            <Center py="xl" px="md">
              <Text size="sm" c="red" ta="center">
                {error}
              </Text>
            </Center>
          )}

          {!loading && !error && notifications.length === 0 && (
            <Center py="xl">
              <Stack align="center" gap={8}>
                <IconBellOff
                  size={32}
                  stroke={1.5}
                  style={{ color: 'var(--mantine-color-gray-4)' }}
                />
                <Text size="sm" c="dimmed">
                  No notifications yet
                </Text>
              </Stack>
            </Center>
          )}

          {!loading && !error && notifications.length > 0 && visibleNotifications.length === 0 && (
            <Center py="xl">
              <Text size="sm" c="dimmed">
                No unread notifications
              </Text>
            </Center>
          )}

          {!loading && !error && visibleNotifications.length > 0 && (
            <Stack gap={0}>
              {visibleNotifications.map((comm) => (
                <NotificationItem
                  key={comm.id ?? comm.sent}
                  comm={comm}
                  clientPortal={isClient}
                  onMarkRead={handleMarkRead}
                  onNavigate={handleNavigate}
                />
              ))}
            </Stack>
          )}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
